import React, { useEffect, useState, useCallback } from 'react';

// ─────────────────────────────────────────────
// DESIGN SYSTEM – mirrors InventoryDashboard.jsx exactly
// (kept here so Purchase Batch / Invoice modules never
//  redefine their own palette or atoms)
// ─────────────────────────────────────────────
export const COLORS = {
  aquaBlue: '#11a8a4',
  grassGreen: '#7ed957',
  darkGrey: '#3c3b3f',
  vividPurple: '#9440dd',
  white: '#ffffff',
  lightGrey: '#f3f4f6',
  mediumGrey: '#e5e7eb',
  darkText: '#1f2937',
  mediumText: '#6b7280',
  lightText: '#9ca3af',
};

// Semantic colors already used throughout InventoryDashboard.jsx
export const SEMANTIC = {
  red: '#dc2626',
  redLight: '#fee2e2',
  redHover: '#b91c1c',
  amber: '#f59e0b',
  amberDark: '#92400e',
  amberLight: '#fef3c7',
  amberBg: '#fffbeb',
  green: '#16a34a',
  greenLight: '#dcfce7',
  blue: '#1d4ed8',
  blueLight: '#dbeafe',
};

export const KEYFRAMES = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
  @keyframes slideIn { from { opacity:0; transform: translateY(-6px); } to { opacity:1; transform: translateY(0); } }
  @keyframes fadeUp { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }
`;

export const formatCurrency = (value) => {
  const n = Number(value || 0);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'Invalid date';
  }
};

export const MONTH_OPTIONS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

export const currentYearOptions = (span = 5) => {
  const now = new Date().getFullYear();
  const years = [];
  for (let y = now; y >= now - span; y--) years.push(y);
  return years;
};

/* ─────────────────────────────────────────────
   GLOBAL STYLE TAG (spin/pulse/etc keyframes)
   ───────────────────────────────────────────── */
export const GlobalKeyframes = () => <style>{KEYFRAMES}</style>;

/* ─────────────────────────────────────────────
   CARD — generic white bordered container
   ───────────────────────────────────────────── */
export const Card = ({ children, style = {} }) => (
  <div
    style={{
      backgroundColor: COLORS.white,
      borderRadius: '8px',
      border: `1px solid ${COLORS.mediumGrey}`,
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
      overflow: 'hidden',
      ...style,
    }}
  >
    {children}
  </div>
);

/* ─────────────────────────────────────────────
   SECTION HEADER — colored bar + title, matches
   "Active Inventory Bags" / "Low Stock Alerts" headers
   ───────────────────────────────────────────── */
export const SectionHeader = ({ title, subtitle, accentColor = COLORS.aquaBlue, right = null }) => (
  <div
    style={{
      padding: '16px 20px',
      borderBottom: `1px solid ${COLORS.mediumGrey}`,
      backgroundColor: COLORS.lightGrey,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      flexWrap: 'wrap',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ width: '4px', height: '18px', backgroundColor: accentColor, borderRadius: '2px', display: 'inline-block' }} />
      <div>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.darkText }}>{title}</h3>
        {subtitle && <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.mediumText }}>{subtitle}</p>}
      </div>
    </div>
    {right}
  </div>
);

/* ─────────────────────────────────────────────
   PAGE HEADER — title + description + refresh button row
   ───────────────────────────────────────────── */
export const PageHeader = ({ title, description, onRefresh, refreshing, children }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    }}
  >
    <div>
      <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: COLORS.darkText }}>{title}</h2>
      {description && <p style={{ margin: 0, color: COLORS.mediumText, fontSize: '14px' }}>{description}</p>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      {children}
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            backgroundColor: COLORS.aquaBlue,
            color: COLORS.white,
            border: 'none',
            borderRadius: '6px',
            padding: '9px 16px',
            cursor: refreshing ? 'default' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: refreshing ? 0.7 : 1,
          }}
          onMouseEnter={(e) => !refreshing && (e.currentTarget.style.backgroundColor = '#0d8f8b')}
          onMouseLeave={(e) => !refreshing && (e.currentTarget.style.backgroundColor = COLORS.aquaBlue)}
        >
          {refreshing ? <Spinner size={14} color={COLORS.white} borderColor="rgba(255,255,255,0.4)" /> : '↻'} Refresh
        </button>
      )}
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   BUTTONS
   ───────────────────────────────────────────── */
export const PrimaryButton = ({ children, onClick, disabled, type = 'button', style = {} }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: COLORS.aquaBlue,
      color: COLORS.white,
      border: 'none',
      borderRadius: '6px',
      padding: '9px 16px',
      cursor: disabled ? 'default' : 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
    onMouseEnter={(e) => !disabled && (e.currentTarget.style.backgroundColor = '#0d8f8b')}
    onMouseLeave={(e) => !disabled && (e.currentTarget.style.backgroundColor = COLORS.aquaBlue)}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ children, onClick, disabled, type = 'button', style = {} }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: COLORS.white,
      color: COLORS.darkText,
      border: `1px solid ${COLORS.mediumGrey}`,
      borderRadius: '6px',
      padding: '9px 16px',
      cursor: disabled ? 'default' : 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
    onMouseEnter={(e) => !disabled && (e.currentTarget.style.backgroundColor = COLORS.lightGrey)}
    onMouseLeave={(e) => !disabled && (e.currentTarget.style.backgroundColor = COLORS.white)}
  >
    {children}
  </button>
);

export const DangerButton = ({ children, onClick, disabled, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: SEMANTIC.red,
      color: COLORS.white,
      border: 'none',
      borderRadius: '5px',
      padding: '4px 10px',
      fontSize: '11px',
      fontWeight: '600',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
    onMouseEnter={(e) => !disabled && (e.currentTarget.style.backgroundColor = SEMANTIC.redHover)}
    onMouseLeave={(e) => !disabled && (e.currentTarget.style.backgroundColor = SEMANTIC.red)}
  >
    {children}
  </button>
);

export const LinkButton = ({ children, onClick, color = COLORS.aquaBlue, style = {} }) => (
  <button
    onClick={onClick}
    style={{
      color,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      padding: 0,
      textDecoration: 'underline',
      ...style,
    }}
  >
    {children}
  </button>
);

/* ─────────────────────────────────────────────
   FORM ATOMS
   ───────────────────────────────────────────── */
const inputBaseStyle = {
  border: `1px solid ${COLORS.mediumGrey}`,
  borderRadius: '6px',
  padding: '8px 10px',
  fontSize: '13px',
  color: COLORS.darkText,
  outline: 'none',
  backgroundColor: COLORS.white,
  fontFamily: "'Inter', sans-serif",
};

export const FieldLabel = ({ children, required }) => (
  <label style={{ fontSize: '12px', fontWeight: '600', color: COLORS.mediumText, marginBottom: '5px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
    {children}{required && <span style={{ color: SEMANTIC.red }}> *</span>}
  </label>
);

export const TextInput = (props) => (
  <input {...props} style={{ ...inputBaseStyle, width: '100%', ...(props.style || {}) }} />
);

export const SelectInput = ({ children, ...props }) => (
  <select {...props} style={{ ...inputBaseStyle, width: '100%', cursor: 'pointer', ...(props.style || {}) }}>
    {children}
  </select>
);

export const TextArea = (props) => (
  <textarea {...props} style={{ ...inputBaseStyle, width: '100%', resize: 'vertical', minHeight: '70px', ...(props.style || {}) }} />
);

export const FilterField = ({ label, children, minWidth = '150px' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth, flex: '1 1 auto' }}>
    <span style={{ fontSize: '11px', fontWeight: '600', color: COLORS.mediumText, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</span>
    {children}
  </div>
);

/* ─────────────────────────────────────────────
   LOADING / ERROR / EMPTY STATES
   ───────────────────────────────────────────── */
export const Spinner = ({ size = 28, color = COLORS.aquaBlue, borderColor = '#e5e7eb' }) => (
  <div
    style={{
      border: `4px solid ${borderColor}`,
      borderTop: `4px solid ${color}`,
      borderRadius: '50%',
      width: `${size}px`,
      height: `${size}px`,
      animation: 'spin 1s linear infinite',
    }}
  />
);

export const SpinnerBlock = ({ color, padding = '40px 0' }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding }}>
    <Spinner color={color} />
  </div>
);

export const SkeletonLine = ({ width = '60px', height = '28px' }) => (
  <div style={{ height, width, backgroundColor: COLORS.mediumGrey, borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
);

export const ErrorBlock = ({ message, onRetry, padding = '24px 0' }) => (
  <div style={{ textAlign: 'center', padding }}>
    <p style={{ color: '#ef4444', marginBottom: '8px', fontSize: '14px' }}>{message || 'Something went wrong.'}</p>
    {onRetry && (
      <button onClick={onRetry} style={{ color: COLORS.aquaBlue, background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
        Retry
      </button>
    )}
  </div>
);

export const EmptyBlock = ({ message = 'No data found.', icon = null, padding = '32px 0' }) => (
  <div style={{ textAlign: 'center', padding, color: COLORS.mediumText, fontSize: '14px' }}>
    {icon && <div style={{ fontSize: '28px', marginBottom: '8px' }}>{icon}</div>}
    {message}
  </div>
);

/* ─────────────────────────────────────────────
   SUMMARY CARD — matches InventoryDashboard.jsx's
   SummaryCard pattern exactly
   ───────────────────────────────────────────── */
export const SummaryCard = ({ title, value, icon, accentColor = COLORS.aquaBlue, loading, error, subtitle }) => (
  <div
    style={{
      backgroundColor: COLORS.white,
      borderRadius: '8px',
      border: `1px solid ${COLORS.mediumGrey}`,
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
      padding: '20px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      overflow: 'hidden',
      flex: '1 1 180px',
      minWidth: '160px',
    }}
  >
    <div style={{ minWidth: 0 }}>
      <p style={{ fontSize: '13px', fontWeight: '500', color: COLORS.mediumText, margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </p>
      {loading ? (
        <SkeletonLine />
      ) : error ? (
        <p style={{ margin: 0, color: '#ef4444', fontSize: '13px' }}>Error</p>
      ) : (
        <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: COLORS.darkText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </p>
      )}
      {subtitle && !loading && !error && (
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.lightText }}>{subtitle}</p>
      )}
    </div>
    <div
      style={{
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        backgroundColor: `${accentColor}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
  </div>
);

export const SummaryCardRow = ({ cards }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
    {cards.map((c, i) => <SummaryCard key={i} {...c} />)}
  </div>
);

/* ─────────────────────────────────────────────
   STATUS BADGE — colored pill, matches existing badges
   ───────────────────────────────────────────── */
const BADGE_PRESETS = {
  AVAILABLE: { bg: SEMANTIC.greenLight, text: SEMANTIC.green },
  'PARTIALLY CONSUMED': { bg: SEMANTIC.amberLight, text: SEMANTIC.amberDark },
  'FULLY CONSUMED': { bg: SEMANTIC.redLight, text: SEMANTIC.red },
  PENDING: { bg: SEMANTIC.amberLight, text: SEMANTIC.amberDark },
  SUBMITTED: { bg: SEMANTIC.blueLight, text: SEMANTIC.blue },
};

export const StatusBadge = ({ status }) => {
  const key = (status || '').toString().toUpperCase();
  const preset = BADGE_PRESETS[key] || { bg: COLORS.lightGrey, text: COLORS.mediumText };
  return (
    <span style={{ backgroundColor: preset.bg, color: preset.text, padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
      {status || 'Unknown'}
    </span>
  );
};

/* ─────────────────────────────────────────────
   PAGINATION
   ───────────────────────────────────────────── */
export const Pagination = ({ page, totalPages, onChange, totalItems, pageSize }) => {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 4px 4px', flexWrap: 'wrap', gap: '8px' }}>
      <span style={{ fontSize: '12px', color: COLORS.mediumText }}>
        Showing {from}–{to} of {totalItems}
      </span>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <button
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          style={{ border: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.white, borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
        >
          ‹ Prev
        </button>
        <span style={{ fontSize: '12px', color: COLORS.darkText, padding: '0 6px' }}>{page} / {totalPages}</span>
        <button
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          style={{ border: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.white, borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
        >
          Next ›
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MODAL SHELL
   ───────────────────────────────────────────── */
export const Modal = ({ title, subtitle, onClose, children, footer, width = '560px' }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}
  >
    <div
      onClick={onClose}
      style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(31,41,55,0.45)' }}
    />
    <div
      style={{
        position: 'relative',
        backgroundColor: COLORS.white,
        borderRadius: '10px',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
        width: '100%',
        maxWidth: width,
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        animation: 'fadeUp 0.18s ease forwards',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div style={{ padding: '18px 22px', borderBottom: `1px solid ${COLORS.mediumGrey}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: COLORS.darkText }}>{title}</h3>
          {subtitle && <p style={{ margin: '3px 0 0', fontSize: '13px', color: COLORS.mediumText }}>{subtitle}</p>}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: COLORS.mediumText, lineHeight: 1, padding: '2px 4px' }}
        >
          ✕
        </button>
      </div>
      <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
        {children}
      </div>
      {footer && (
        <div style={{ padding: '14px 22px', borderTop: `1px solid ${COLORS.mediumGrey}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          {footer}
        </div>
      )}
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   TOASTS — lightweight success/error feedback
   ───────────────────────────────────────────── */
export const useToasts = () => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const pushToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => removeToast(id), 4000);
  }, [removeToast]);

  return { toasts, pushToast, removeToast };
};

export const ToastStack = ({ toasts, removeToast }) => (
  <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1100, display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {toasts.map((t) => {
      const isSuccess = t.type === 'success';
      const isError = t.type === 'error';
      return (
        <div
          key={t.id}
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${isSuccess ? '#bbf7d0' : isError ? '#fecaca' : COLORS.mediumGrey}`,
            borderLeft: `4px solid ${isSuccess ? SEMANTIC.green : isError ? SEMANTIC.red : COLORS.aquaBlue}`,
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.15)',
            padding: '12px 16px',
            minWidth: '260px',
            maxWidth: '360px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            fontSize: '13px',
            color: COLORS.darkText,
            animation: 'slideIn 0.2s ease forwards',
          }}
        >
          <span>{isSuccess ? '✅' : isError ? '⚠️' : 'ℹ️'}</span>
          <span style={{ flex: 1 }}>{t.message}</span>
          <button onClick={() => removeToast(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.lightText, fontSize: '13px' }}>✕</button>
        </div>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────────
   CONFIRM DIALOG — inline-style version
   ───────────────────────────────────────────── */
export const ConfirmDialog = ({ open, title = 'Are you sure?', message, onConfirm, onCancel, loading, confirmLabel = 'Confirm' }) => {
  if (!open) return null;
  return (
    <Modal title={title} onClose={onCancel} width="420px" footer={(
      <>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        <PrimaryButton onClick={onConfirm} disabled={loading} style={{ backgroundColor: SEMANTIC.red }}>
          {loading && <Spinner size={13} color={COLORS.white} borderColor="rgba(255,255,255,0.4)" />} {confirmLabel}
        </PrimaryButton>
      </>
    )}>
      <p style={{ margin: 0, fontSize: '14px', color: COLORS.mediumText, lineHeight: 1.5 }}>{message}</p>
    </Modal>
  );
};

/* ─────────────────────────────────────────────
   SUB-TAB BAR — pill-style sub-navigation used inside
   a module (e.g. Monthly / Camp / Bag invoice)
   ───────────────────────────────────────────── */
export const SubTabBar = ({ tabs, activeTab, onChange }) => (
  <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
    {tabs.map((tab) => {
      const isActive = activeTab === tab.id;
      return (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border: `1px solid ${isActive ? COLORS.aquaBlue : COLORS.mediumGrey}`,
            backgroundColor: isActive ? COLORS.aquaBlue : COLORS.white,
            color: isActive ? COLORS.white : COLORS.mediumText,
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.15s',
          }}
        >
          {tab.icon} {tab.label}
        </button>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────────
   Hook: paginate a client-side array
   ───────────────────────────────────────────── */
export const useClientPagination = (items, pageSize = 10) => {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [items.length]);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = items.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { page: safePage, setPage, totalPages, pageItems, totalItems: items.length, pageSize };
};
